import { useEffect } from 'react';

export type UseScrollToItemOnHashOptions = {
  /**
   * Data attribute whose value is matched against the URL hash (without the
   * leading `#`). The element carrying `[<attribute>="<hash>"]` is scrolled to.
   */
  readonly attribute?: string;
  /** `scrollIntoView` behaviour. */
  readonly behavior?: ScrollBehavior;
  /** `scrollIntoView` block alignment. */
  readonly block?: ScrollLogicalPosition;
  /** How many times to re-check the DOM before giving up. */
  readonly maxAttempts?: number;
  /** Delay between DOM re-checks, in milliseconds. */
  readonly retryDelayMs?: number;
};

const DEFAULT_ATTRIBUTE = 'data-hash-identifier';
const DEFAULT_BEHAVIOR: ScrollBehavior = 'smooth';
const DEFAULT_BLOCK: ScrollLogicalPosition = 'center';
const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_RETRY_DELAY_MS = 100;

/**
 * On mount, reads the current URL hash and scrolls the element tagged with
 * `[data-hash-identifier="<hash>"]` into view, retrying a few times while the
 * target is still being rendered. No-op when there is no hash.
 */
export function useScrollToItemOnHash(options: UseScrollToItemOnHashOptions = {}): void {
  const {
    attribute = DEFAULT_ATTRIBUTE,
    behavior = DEFAULT_BEHAVIOR,
    block = DEFAULT_BLOCK,
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    retryDelayMs = DEFAULT_RETRY_DELAY_MS,
  } = options;

  useEffect(() => {
    const hash = window.location.hash;

    if (hash.length <= 1) {
      return;
    }

    const identifier = hash.slice(1);
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const scrollToElement = (attempts = 0): void => {
      const element = document.querySelector(`[${attribute}="${identifier}"]`);

      if (element) {
        element.scrollIntoView({ behavior, block });
        return;
      }

      if (attempts < maxAttempts) {
        retryTimer = setTimeout(() => scrollToElement(attempts + 1), retryDelayMs);
      }
    };

    scrollToElement();

    return () => {
      if (retryTimer !== undefined) {
        clearTimeout(retryTimer);
      }
    };
  }, [attribute, behavior, block, maxAttempts, retryDelayMs]);
}
